import React from 'react';
import * as XLSX from 'xlsx';
import { HOST } from '../../config/config';
import { DataGrid } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { PdfViewer } from '../../layouts/customers/PdfViewer';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';

const styles = {
    modal: {
        width: '100vh',
        height: '100vh',
        maxWidth: '90vh',
        maxHeight: '90vh',
        top: '50%',
        left: '50%',
        boxShadow: 24,
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
    },
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: '#2464EB',
    },
    headerContent: {
        p: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    fileName: {
        color: 'white',
        flex: 1,
        mx: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    iconButton: {
        color: 'white',
        '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.1)',
        },
    },
};

export const ImagePreviewModal = ({ open, onClose, file, attachment, fileName }) => {
    const docViewerRef = React.useRef(null);
    const [error, setError] = React.useState(null);

    const fileUrl = React.useMemo(() => fileUtils.getFileUrl(file, attachment), [file, attachment]);
    const fileExtension = React.useMemo(() => fileUtils.getFileExtension(fileName), [fileName]);

    const docs = React.useMemo(() => ([{
        uri: fileUrl,
        fileName,
        fileType: fileExtension?.toLowerCase(),
        mimeType: fileUtils.getMimeType(fileExtension)
    }]), [fileUrl, fileName, fileExtension]);

    const handleDownload = () => fileUtils.downloadFile(fileUrl, fileName);
    const handleError = (error) => {
        console.error('DocViewer Error:', error);
        setError('Unable to load the file. Please try downloading it instead.');
    };

    const renderContent = () => {
        if (error) {
            return <Box sx={{ textAlign: 'center', color: 'error.main' }}>{error}</Box>;
        }

        const fileType = fileExtension?.toLowerCase();

        if (fileType === 'pdf') {
            return <PdfViewer pdfFile={fileUrl} onClose={() => onClose(false)} />;
        }

        if (['xlsx', 'xls'].includes(fileType)) {
            return <ExcelViewer fileUrl={fileUrl} />;
        }

        if ([...FILE_TYPES.VIDEO, ...FILE_TYPES.AUDIO, ...FILE_TYPES.IMAGE].includes(fileType)) {
            return <MediaViewer fileUrl={fileUrl} fileExtension={fileExtension} />;
        }

        if (FILE_TYPES.DOCUMENT.includes(fileType)) {
            return (
                <DocViewer
                    documents={docs}
                    ref={docViewerRef}
                    pluginRenderers={DocViewerRenderers}
                    style={{ maxWidth: '100%', height: '100%' }}
                    theme={docViewerConfig.theme}
                    config={docViewerConfig.config}
                    onError={handleError}
                />
            );
        }

        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                This file type is not supported for preview. Please download to view.
            </Box>
        );
    };

    return (
        <Modal open={open}>
            <Box sx={{ ...styles.modal, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 100px)', pb: 2 }}>
                <CustomHeaderComponent
                    fileName={fileName}
                    onClose={onClose}
                    downloadFile={handleDownload}
                />
                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                    {renderContent()}
                </Box>
            </Box>
        </Modal>
    );
};

const FILE_TYPES = {
    IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'],
    DOCUMENT: ['pdf', 'doc', 'docx', 'xlsx', 'xls', 'ppt', 'pptx', 'txt', 'csv'],
    VIDEO: ['mp4', 'webm', 'ogg'],
    AUDIO: ['mp3', 'wav', 'ogg'],
};

const docViewerConfig = {
    theme: {
        primary: '#2464EB',
        tertiary: '#e8f3ff',
        secondary: '#ffffff',
        textPrimary: '#ffffff',
        textTertiary: '#00000099',
        textSecondary: '#00000099',
        disableThemeScrollbar: false,
    },
    config: {
        header: {
            disableHeader: true,
            disableFileName: false,
            retainURLParams: false,
        },
    },
};

const fileUtils = {
    getFileUrl: (file, attachment) => (
        file ? window.URL.createObjectURL(file) : attachment ? `${HOST}${attachment}` : null
    ),
    getFileExtension: (fileName) => fileName?.split('.').pop(),
    downloadFile: (fileUrl, fileName) => {
        if (!fileUrl) return;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },
    getMimeType: (extension) => {
        const mimeTypes = {
            // Images
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            gif: 'image/gif',
            bmp: 'image/bmp',
            webp: 'image/webp',
            // Documents
            pdf: 'application/pdf',
            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            xls: 'application/vnd.ms-excel',
            ppt: 'application/vnd.ms-powerpoint',
            pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            txt: 'text/plain',
            csv: 'text/csv',
            // Video
            mp4: 'video/mp4',
            webm: 'video/webm',
            // Audio
            mp3: 'audio/mpeg',
            wav: 'audio/wav',
            ogg: 'audio/ogg',
        };
        return mimeTypes[extension?.toLowerCase()] || 'application/octet-stream';
    },
    isFileType: (extension, typeArray) => {
        return typeArray.includes(extension?.toLowerCase());
    }
};

const MediaViewer = ({ fileUrl, fileExtension }) => {
    if (fileUtils.isFileType(fileExtension, FILE_TYPES.VIDEO)) {
        return (
            <video controls style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto' }}>
                <source src={fileUrl} type={fileUtils.getMimeType(fileExtension)} />
                Your browser does not support the video tag.
            </video>
        );
    }
};

const ExcelViewer = ({ fileUrl }) => {
    const [rows, setRows] = React.useState([]);
    const [columns, setColumns] = React.useState([]);
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 10,
        page: 0,
    });

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(fileUrl);
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                // Enhanced column configuration
                const columnConfig = jsonData[0].map((col, index) => ({
                    field: `col${index}`,
                    headerName: col?.toString() || `Column ${index + 1}`,
                    flex: 1,
                    minWidth: 150,
                    renderCell: (params) => (
                        <div style={{
                            whiteSpace: 'normal',
                            lineHeight: '1.2em',
                            padding: '8px 0'
                        }}>
                            {params.value}
                        </div>
                    )
                }));
                setColumns(columnConfig);

                // Format rows with proper handling of empty cells
                const formattedRows = jsonData.slice(1).map((row, index) => ({
                    id: index,
                    ...columnConfig.reduce((acc, col, colIndex) => {
                        acc[`col${colIndex}`] = row[colIndex] ?? '';
                        return acc;
                    }, {})
                }));
                setRows(formattedRows);
            } catch (err) {
                console.error('Error loading Excel file:', err);
            }
        };
        fetchData();
    }, [fileUrl]);

    return (
        <Box sx={{ width: '100%', height: '100%', }}>
            <DataGrid
                rows={rows}
                columns={columns}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                autoHeight
                sx={{
                    '& .MuiDataGrid-cell': {
                        padding: '8px',
                        wordBreak: 'break-word'
                    },
                    '& .MuiDataGrid-columnHeader': {
                        padding: '8px',
                        backgroundColor: '#f5f5f5'
                    },
                    '& .MuiDataGrid-row': {
                        minHeight: '100%'
                    }
                }}
            />
        </Box>
    );
};

const CustomHeaderComponent = ({ fileName, onClose, downloadFile }) => {
    return (
        <Box sx={styles.header}>
            <Box sx={styles.headerContent}>
                <Typography variant="subtitle1" sx={styles.fileName}>
                    {fileName}
                </Typography>
                <IconButton
                    onClick={downloadFile}
                    sx={styles.iconButton}
                    aria-label="download file"
                >
                    <DownloadIcon />
                </IconButton>
                <IconButton
                    onClick={onClose}
                    sx={styles.iconButton}
                    aria-label="close modal"
                >
                    <CloseIcon />
                </IconButton>
            </Box>
        </Box>
    );
};
